import i18n from '@/i18n';

// Бот и keymanager считают резервный трафик в байтах, пользователю показываем ГБ.
const BYTES_IN_GB = 1024 ** 3;

export function useBytes() {
	const toGb = (bytes: number | null | undefined): number => {
		return (bytes ?? 0) / BYTES_IN_GB;
	};

	/**
	 * «48», «12,5», «0,3» — не больше одного знака после запятой.
	 *
	 * Разделитель берётся из текущей локали интерфейса, а не из локали устройства:
	 * цифры в строке «Осталось X из Y ГБ» должны выглядеть так же, как остальные
	 * числа на странице. Неизвестная локаль не должна ронять рендер, отсюда catch.
	 */
	const formatGb = (bytes: number | null | undefined): string => {
		const value = toGb(bytes);
		try {
			return new Intl.NumberFormat(i18n.global.locale.value, { maximumFractionDigits: 1 }).format(value);
		} catch {
			return String(Math.round(value * 10) / 10);
		}
	};

	// От 1000 ГБ пишем в терабайтах: «1 340 ГБ» читается хуже, чем «1,3 ТБ».
	// Порог десятичный, как его называет человек, а не 1024.
	const TB_THRESHOLD_GB = 1000;

	const formatNumber = (value: number): string => {
		try {
			return new Intl.NumberFormat(i18n.global.locale.value, { maximumFractionDigits: 1 }).format(value);
		} catch {
			return String(Math.round(value * 10) / 10);
		}
	};

	// «47 ГБ», «1,3 ТБ» — число с единицей
	const formatTraffic = (bytes: number | null | undefined): string => {
		const gb = toGb(bytes);
		if (gb >= TB_THRESHOLD_GB) {
			return `${formatNumber(gb / 1000)} ${i18n.global.t('common.units.tb')}`;
		}
		return `${formatNumber(gb)} ${i18n.global.t('common.units.gb')}`;
	};

	// «3,4 из 10 ГБ» — обе цифры в единице лимита, чтобы единица не повторялась
	const formatTrafficPair = (usedBytes: number | null | undefined, limitBytes: number | null | undefined): string => {
		const limitGb = toGb(limitBytes);
		const inTb = limitGb >= TB_THRESHOLD_GB;
		const divisor = inTb ? 1000 : 1;
		return i18n.global.t('common.units.pair', {
			used: formatNumber(toGb(usedBytes) / divisor),
			total: formatNumber(limitGb / divisor),
			unit: i18n.global.t(inTb ? 'common.units.tb' : 'common.units.gb')
		});
	};

	return {
		toGb,
		formatGb,
		formatTraffic,
		formatTrafficPair
	};
}
